/**
 * UserFeed.js
 *
 * The feed page showing all gift pools in the Gifted community.
 * Displays platform statistics and a feed of gift pool posts.
 * Users can interact with posts through likes, comments, shares, and gifts.
 *
 * Route: / (home) and /feed
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SERVER_URL } from './config';
import { useAuth } from './AuthContext';
import Navigation from './components/Navigation';
import GiftPoolPost from './components/GiftPoolPost';
import { addComment, loadComments, deleteComment, editComment } from './utils/commentUtils';
import { CATEGORY_GROUPS } from './utils/constants';
import Avatar from './components/Avatar';
import GiftOptionsModal from './components/GiftOptionsModal';

function UserFeed() {
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState('');
  const [showGiftOptionsModal, setShowGiftOptionsModal] = useState(false);

  // Use authenticated user's email and name if available
  const currentUserEmail = user?.email || '';
  const currentUserName = user?.username || '';
  const [sharedPosts, setSharedPosts] = useState(new Set());
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [vouchedPosts, setVouchedPosts] = useState(new Set());
  const [commentedPosts, setCommentedPosts] = useState(new Set());
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [replyingTo, setReplyingTo] = useState({});
  const [replyText, setReplyText] = useState({});
  const [donors, setDonors] = useState({});
  const [expandedDonors, setExpandedDonors] = useState(new Set());
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const [expandedLikes, setExpandedLikes] = useState(new Set());
  const [likers, setLikers] = useState({});
  const [expandedShares, setExpandedShares] = useState(new Set());
  const [sharers, setSharers] = useState({});
  const [expandedVouches, setExpandedVouches] = useState(new Set());
  const [vouchers, setVouchers] = useState({});
  const [followingUsers, setFollowingUsers] = useState(new Set());
  const [followLoading, setFollowLoading] = useState({});
  const [followingStatusLoaded, setFollowingStatusLoaded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showTimeSensitiveOnly, setShowUrgentOnly] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [feedSeed, setFeedSeed] = useState(() => crypto.randomUUID());

  const toggleGroup = label => setExpandedGroups(prev => {
    const next = new Set(prev);
    next.has(label) ? next.delete(label) : next.add(label);
    return next;
  });

  // Function to load posts with pagination
  const loadPosts = async (isInitial = false) => {
    if (!isInitial && (loadingMore || !hasMore)) return;

    let currentSeed = feedSeed;
    if (isInitial) {
      setLoading(true);
      setOffset(0);
      setPosts([]);
      currentSeed = crypto.randomUUID(); // new seed → new ordering on each refresh
      setFeedSeed(currentSeed);
    } else {
      setLoadingMore(true);
    }

    try {
      const currentOffset = isInitial ? 0 : offset;
      let url = `${SERVER_URL}/posts?limit=20&offset=${currentOffset}&feed_seed=${currentSeed}`;

      if (showTimeSensitiveOnly) {
        url += `&is_time_sensitive=true`;
      }
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (isInitial) {
        setPosts(data.posts);
        setHasMore(data.has_more);
        setOffset(data.posts.length);
      } else {
        // For pagination, filter out any duplicates before adding
        setPosts(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const newPosts = data.posts.filter(p => !existingIds.has(p.id));
          return [...prev, ...newPosts];
        });
        setHasMore(data.has_more);
        setOffset(currentOffset + data.posts.length);
      }
    } catch (err) {
      console.error('Error loading posts:', err);
      setError('Failed to load posts: ' + err.message);
    } finally {
      if (isInitial) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    // Don't run until auth has finished loading, so we don't
    // prematurely mark followingStatusLoaded as true
    if (authLoading) return;

    setFollowingStatusLoaded(false);
    setFollowingUsers(new Set());
    // Load following status for authenticated users
    if (isAuthenticated && user) {
      fetch(`${SERVER_URL}/users/${user.id}/following`)
        .then(res => res.json())
        .then(data => {
          const followingIds = new Set(data.following.map(u => u.id));
          setFollowingUsers(followingIds);
          setFollowingStatusLoaded(true);
        })
        .catch(err => {
          console.error('Error loading following list:', err);
          setFollowingStatusLoaded(true);
        });
    } else {
      setFollowingStatusLoaded(true);
    }

    // Load initial posts
    loadPosts(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, user]);


  // Reload posts when time-sensitive filter changes (server-side filter)
  useEffect(() => {
    if (!authLoading) {
      loadPosts(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTimeSensitiveOnly]);

  // Reload posts when returning from post creation
  useEffect(() => {
    if (location.state?.message) {
      const newPost = location.state?.newPost;
      loadPosts(true).then(() => {
        if (newPost) {
          setPosts(prev => [newPost, ...prev.filter(p => p.id !== newPost.id)]);
        }
      });
      // Clear the state so we don't reload on every render
      window.history.replaceState({}, document.title);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Load liked, shared, and vouched status when posts change
  useEffect(() => {
    if (currentUserEmail && posts.length > 0) {
      Promise.all([
        // Load liked posts
        Promise.all(
          posts.map(post =>
            fetch(
              `${SERVER_URL}/posts/${post.id}/liked?user_email=${encodeURIComponent(currentUserEmail)}`
            )
              .then(res => res.json())
              .then(data => ({ postId: post.id, liked: data.liked }))
              .catch(() => ({ postId: post.id, liked: false }))
          )
        ),
        // Load shared posts
        Promise.all(
          posts.map(post =>
            fetch(
              `${SERVER_URL}/posts/${post.id}/shared?user_email=${encodeURIComponent(currentUserEmail)}`
            )
              .then(res => res.json())
              .then(data => ({ postId: post.id, shared: data.shared }))
              .catch(() => ({ postId: post.id, shared: false }))
          )
        ),
        // Load vouched posts
        Promise.all(
          posts.map(post =>
            fetch(
              `${SERVER_URL}/posts/${post.id}/vouched?user_email=${encodeURIComponent(currentUserEmail)}`
            )
              .then(res => res.json())
              .then(data => ({ postId: post.id, vouched: data.vouched }))
              .catch(() => ({ postId: post.id, vouched: false }))
          )
        ),
      // Load commented posts
      Promise.all(
        posts.map(post =>
          fetch(
            `${SERVER_URL}/posts/${post.id}/commented?user_email=${encodeURIComponent(currentUserEmail)}`
          )
            .then(res => res.json())
            .then(data => ({ postId: post.id, commented: data.commented }))
            .catch(() => ({ postId: post.id, commented: false }))
        )
      ),
      ]).then(([likedResults, sharedResults, vouchedResults, commentedResults]) => {
        const liked = new Set(likedResults.filter(r => r.liked).map(r => r.postId));
        const shared = new Set(sharedResults.filter(r => r.shared).map(r => r.postId));
        const vouched = new Set(vouchedResults.filter(r => r.vouched).map(r => r.postId));
        const commented = new Set(commentedResults.filter(r => r.commented).map(r => r.postId));
        setLikedPosts(liked);
        setSharedPosts(shared);
        setVouchedPosts(vouched);
        setCommentedPosts(commented);
      });
    }
  }, [currentUserEmail, posts]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      // Check if user has scrolled near the bottom
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      // Load more when user is 500px from bottom
      if (scrollHeight - scrollTop - clientHeight < 500 && !loadingMore && hasMore) {
        loadPosts(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore, hasMore, offset]);

  const handleFollowToggle = async targetUserId => {
    if (!isAuthenticated || !user) {
      alert('Please log in to follow users');
      return;
    }

    setFollowLoading(prev => ({ ...prev, [targetUserId]: true }));
    try {
      const isFollowing = followingUsers.has(targetUserId);
      const url = `${SERVER_URL}/users/${targetUserId}/follow?follower_email=${encodeURIComponent(user.email)}`;
      const method = isFollowing ? 'DELETE' : 'POST';

      const response = await fetch(url, { method });

      if (!response.ok) {
        let errorMessage = 'Failed to update follow status';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch (e) {
          // If response is not JSON, use default message
        }
        throw new Error(errorMessage);
      }

      // Update local state
      const newFollowing = new Set(followingUsers);
      if (isFollowing) {
        newFollowing.delete(targetUserId);
      } else {
        newFollowing.add(targetUserId);
      }
      setFollowingUsers(newFollowing);
    } catch (err) {
      console.error('Error toggling follow:', err);
      alert(typeof err === 'string' ? err : err.message || 'Failed to update follow status');
    } finally {
      setFollowLoading(prev => ({ ...prev, [targetUserId]: false }));
    }
  };

  const handleShare = async post => {
    if (!currentUserEmail) {
      // Redirect to login if not authenticated
      navigate('/login');
      return;
    }

    const isAlreadyShared = sharedPosts.has(post.id);

    if (isAlreadyShared) {
      await unsharePost(post.id);
    } else {
      await sharePost(post.id);
    }
  };

  const sharePost = async postId => {
    try {
      const url = `${SERVER_URL}/posts/${postId}/share?user_email=${encodeURIComponent(currentUserEmail)}`;

      const response = await fetch(url, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Share error:', errorData);
        throw new Error(errorData.detail || 'Failed to share');
      }

      const data = await response.json();

      // Update local state
      setSharedPosts(new Set([...sharedPosts, postId]));

      // Update post share count
      setPosts(
        posts.map(post => (post.id === postId ? { ...post, share_count: data.share_count } : post))
      );

    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  const unsharePost = async postId => {
    try {
      const url = `${SERVER_URL}/posts/${postId}/share?user_email=${encodeURIComponent(currentUserEmail)}`;
      const response = await fetch(url, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to unshare');
      }

      const data = await response.json();

      // Update local state
      const newSharedPosts = new Set(sharedPosts);
      newSharedPosts.delete(postId);
      setSharedPosts(newSharedPosts);

      // Update post share count
      setPosts(
        posts.map(post => (post.id === postId ? { ...post, share_count: data.share_count } : post))
      );

    } catch (err) {
      console.error('Error unsharing:', err);
    }
  };

  const handleLike = async post => {
    if (!currentUserEmail) {
      // Redirect to login if not authenticated
      navigate('/login');
      return;
    }

    const isAlreadyLiked = likedPosts.has(post.id);

    if (isAlreadyLiked) {
      await unlikePost(post.id);
    } else {
      await likePost(post.id);
    }
  };

  const likePost = async postId => {
    try {
      const url = `${SERVER_URL}/posts/${postId}/like?user_email=${encodeURIComponent(currentUserEmail)}`;
      const response = await fetch(url, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to like');
      }

      const data = await response.json();

      // Update local state
      setLikedPosts(new Set([...likedPosts, postId]));

      // Update post like count
      setPosts(
        posts.map(post => (post.id === postId ? { ...post, like_count: data.like_count } : post))
      );

      // Reload likers list if it has been loaded before (either currently expanded or was expanded before)
      if (likers[postId] || expandedLikes.has(postId)) {
        try {
          const likersResponse = await fetch(`${SERVER_URL}/posts/${postId}/likers`);
          if (likersResponse.ok) {
            const likersData = await likersResponse.json();
            setLikers({ ...likers, [postId]: likersData.likers });
          }
        } catch (err) {
          console.error('Error reloading likers:', err);
        }
      }
    } catch (err) {
      console.error('Error liking:', err);
      if (!err.message.includes('Already liked')) {
        alert(err.message);
      }
    }
  };

  const unlikePost = async postId => {
    try {
      const url = `${SERVER_URL}/posts/${postId}/like?user_email=${encodeURIComponent(currentUserEmail)}`;
      const response = await fetch(url, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to unlike');
      }

      const data = await response.json();

      // Update local state
      const newLikedPosts = new Set(likedPosts);
      newLikedPosts.delete(postId);
      setLikedPosts(newLikedPosts);

      // Update post like count
      setPosts(
        posts.map(post => (post.id === postId ? { ...post, like_count: data.like_count } : post))
      );

      // Reload likers list if it has been loaded before (either currently expanded or was expanded before)
      if (likers[postId] || expandedLikes.has(postId)) {
        try {
          const likersResponse = await fetch(`${SERVER_URL}/posts/${postId}/likers`);
          if (likersResponse.ok) {
            const likersData = await likersResponse.json();
            setLikers({ ...likers, [postId]: likersData.likers });
          }
        } catch (err) {
          console.error('Error reloading likers:', err);
        }
      }
    } catch (err) {
      console.error('Error unliking:', err);
      alert(err.message);
    }
  };

  const toggleComments = async postId => {
    const isExpanded = expandedComments.has(postId);

    if (isExpanded) {
      // Collapse comments
      const newExpanded = new Set(expandedComments);
      newExpanded.delete(postId);
      setExpandedComments(newExpanded);
    } else {
      // Close all other sections for this post
      const newExpandedDonors = new Set(expandedDonors);
      newExpandedDonors.delete(postId);
      setExpandedDonors(newExpandedDonors);

      const newExpandedLikes = new Set(expandedLikes);
      newExpandedLikes.delete(postId);
      setExpandedLikes(newExpandedLikes);

      const newExpandedShares = new Set(expandedShares);
      newExpandedShares.delete(postId);
      setExpandedShares(newExpandedShares);

      const newExpandedVouches = new Set(expandedVouches);
      newExpandedVouches.delete(postId);
      setExpandedVouches(newExpandedVouches);

      // Expand and load comments
      const newExpanded = new Set(expandedComments);
      newExpanded.add(postId);
      setExpandedComments(newExpanded);

      // Fetch comments
      await loadComments(postId, comments, setComments);
    }
  };

  const handleAddComment = async postId => {
    if (!currentUserEmail) {
      // Redirect to login if not authenticated
      navigate('/login');
      return;
    }

    const commentText = newComment[postId];

    await addComment(
      postId,
      commentText,
      currentUserName,
      currentUserEmail,
      comments,
      setComments,
      newComment,
      setNewComment,
      // Callback to update comment count in posts
      postId => {
        setPosts(
          posts.map(post =>
            post.id === postId ? { ...post, comment_count: (post.comment_count || 0) + 1 } : post
          )
        );
        setCommentedPosts(prev => new Set([...prev, postId]));
      }
    );
  };

  const handleReplyClick = (postId, commentId, userName) => {
    if (!currentUserEmail) {
      navigate('/login');
      return;
    }
    // Toggle reply input: if already replying to this comment, close it
    if (replyingTo[postId] === commentId) {
      setReplyingTo({ ...replyingTo, [postId]: null });
      setReplyText({ ...replyText, [postId]: '' });
    } else {
      setReplyingTo({ ...replyingTo, [postId]: commentId });
      setReplyText({ ...replyText, [postId]: `@${userName} ` });
    }
  };

  const handleAddReply = async (postId, parentId) => {
    const text = replyText[postId];
    if (!text || !text.trim()) return;

    await addComment(
      postId,
      text,
      currentUserName,
      currentUserEmail,
      comments,
      setComments,
      replyText,
      setReplyText,
      postId => {
        setPosts(
          posts.map(post =>
            post.id === postId ? { ...post, comment_count: (post.comment_count || 0) + 1 } : post
          )
        );
      },
      parentId
    );
    setReplyingTo({ ...replyingTo, [postId]: null });
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    await deleteComment(commentId, currentUserEmail, postId, comments, setComments, postId => {
      setPosts(
        posts.map(post =>
          post.id === postId ? { ...post, comment_count: Math.max((post.comment_count || 1) - 1, 0) } : post
        )
      );
    });
  };

  const handleEditComment = async (postId, commentId, content) => {
    if (!content || !content.trim()) return;
    await editComment(commentId, currentUserEmail, content, postId, comments, setComments);
  };

  const toggleDonors = async postId => {
    const isExpanded = expandedDonors.has(postId);

    if (isExpanded) {
      // Collapse donors
      const newExpanded = new Set(expandedDonors);
      newExpanded.delete(postId);
      setExpandedDonors(newExpanded);
    } else {
      // Close all other sections for this post
      const newExpandedComments = new Set(expandedComments);
      newExpandedComments.delete(postId);
      setExpandedComments(newExpandedComments);

      const newExpandedLikes = new Set(expandedLikes);
      newExpandedLikes.delete(postId);
      setExpandedLikes(newExpandedLikes);

      const newExpandedShares = new Set(expandedShares);
      newExpandedShares.delete(postId);
      setExpandedShares(newExpandedShares);

      // Expand and load donors
      const newExpanded = new Set(expandedDonors);
      newExpanded.add(postId);
      setExpandedDonors(newExpanded);

      // Fetch donors if not already loaded
      if (!donors[postId]) {
        try {
          const response = await fetch(`${SERVER_URL}/posts/${postId}/donors`);
          if (response.ok) {
            const data = await response.json();
            setDonors({ ...donors, [postId]: data.donors });
          }
        } catch (err) {
          console.error('Error loading donors:', err);
        }
      }
    }
  };

  const toggleLikes = async postId => {
    const isExpanded = expandedLikes.has(postId);

    if (isExpanded) {
      // Collapse likes
      const newExpanded = new Set(expandedLikes);
      newExpanded.delete(postId);
      setExpandedLikes(newExpanded);
    } else {
      // Close all other sections for this post
      const newExpandedComments = new Set(expandedComments);
      newExpandedComments.delete(postId);
      setExpandedComments(newExpandedComments);

      const newExpandedDonors = new Set(expandedDonors);
      newExpandedDonors.delete(postId);
      setExpandedDonors(newExpandedDonors);

      const newExpandedShares = new Set(expandedShares);
      newExpandedShares.delete(postId);
      setExpandedShares(newExpandedShares);

      const newExpandedVouches2 = new Set(expandedVouches);
      newExpandedVouches2.delete(postId);
      setExpandedVouches(newExpandedVouches2);

      // Expand and load likes
      const newExpanded = new Set(expandedLikes);
      newExpanded.add(postId);
      setExpandedLikes(newExpanded);

      // Fetch likers if not already loaded
      if (!likers[postId]) {
        try {
          const response = await fetch(`${SERVER_URL}/posts/${postId}/likers`);
          if (response.ok) {
            const data = await response.json();
            setLikers({ ...likers, [postId]: data.likers });
          }
        } catch (err) {
          console.error('Error loading likers:', err);
        }
      }
    }
  };

  const toggleShares = async postId => {
    const isExpanded = expandedShares.has(postId);

    if (isExpanded) {
      // Collapse shares
      const newExpanded = new Set(expandedShares);
      newExpanded.delete(postId);
      setExpandedShares(newExpanded);
    } else {
      // Close all other sections for this post
      const newExpandedComments = new Set(expandedComments);
      newExpandedComments.delete(postId);
      setExpandedComments(newExpandedComments);

      const newExpandedDonors = new Set(expandedDonors);
      newExpandedDonors.delete(postId);
      setExpandedDonors(newExpandedDonors);

      const newExpandedLikes = new Set(expandedLikes);
      newExpandedLikes.delete(postId);
      setExpandedLikes(newExpandedLikes);

      const newExpandedVouches = new Set(expandedVouches);
      newExpandedVouches.delete(postId);
      setExpandedVouches(newExpandedVouches);

      // Expand and load shares
      const newExpanded = new Set(expandedShares);
      newExpanded.add(postId);
      setExpandedShares(newExpanded);

      // Fetch sharers if not already loaded
      if (!sharers[postId]) {
        try {
          const response = await fetch(`${SERVER_URL}/posts/${postId}/sharers`);
          if (response.ok) {
            const data = await response.json();
            setSharers({ ...sharers, [postId]: data.sharers });
          }
        } catch (err) {
          console.error('Error loading sharers:', err);
        }
      }
    }
  };

  const handleVouch = async post => {
    if (!currentUserEmail) {
      navigate('/login');
      return;
    }

    const isAlreadyVouched = vouchedPosts.has(post.id);

    if (isAlreadyVouched) {
      await unvouchPost(post.id);
    } else {
      await vouchPost(post.id);
    }
  };

  const vouchPost = async postId => {
    try {
      const url = `${SERVER_URL}/posts/${postId}/vouch?user_email=${encodeURIComponent(currentUserEmail)}`;
      const response = await fetch(url, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to vouch');
      }

      const data = await response.json();

      // Update local state
      setVouchedPosts(new Set([...vouchedPosts, postId]));

      // Update post vouch count
      setPosts(
        posts.map(post => (post.id === postId ? { ...post, vouch_count: data.vouch_count } : post))
      );

      // Reload vouchers list if it has been loaded before
      if (vouchers[postId] || expandedVouches.has(postId)) {
        try {
          const vouchersResponse = await fetch(`${SERVER_URL}/posts/${postId}/vouchers`);
          if (vouchersResponse.ok) {
            const vouchersData = await vouchersResponse.json();
            setVouchers({ ...vouchers, [postId]: vouchersData.vouchers });
          }
        } catch (err) {
          console.error('Error reloading vouchers:', err);
        }
      }
    } catch (err) {
      console.error('Error vouching:', err);
      if (!err.message.includes('Already vouched')) {
        alert(err.message);
      }
    }
  };

  const unvouchPost = async postId => {
    try {
      const url = `${SERVER_URL}/posts/${postId}/vouch?user_email=${encodeURIComponent(currentUserEmail)}`;
      const response = await fetch(url, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to unvouch');
      }

      const data = await response.json();

      // Update local state
      const newVouchedPosts = new Set(vouchedPosts);
      newVouchedPosts.delete(postId);
      setVouchedPosts(newVouchedPosts);

      // Update post vouch count
      setPosts(
        posts.map(post => (post.id === postId ? { ...post, vouch_count: data.vouch_count } : post))
      );

      // Reload vouchers list if it has been loaded before
      if (vouchers[postId] || expandedVouches.has(postId)) {
        try {
          const vouchersResponse = await fetch(`${SERVER_URL}/posts/${postId}/vouchers`);
          if (vouchersResponse.ok) {
            const vouchersData = await vouchersResponse.json();
            setVouchers({ ...vouchers, [postId]: vouchersData.vouchers });
          }
        } catch (err) {
          console.error('Error reloading vouchers:', err);
        }
      }
    } catch (err) {
      console.error('Error unvouching:', err);
      alert(err.message);
    }
  };

  const toggleVouches = async postId => {
    const isExpanded = expandedVouches.has(postId);

    if (isExpanded) {
      // Collapse vouches
      const newExpanded = new Set(expandedVouches);
      newExpanded.delete(postId);
      setExpandedVouches(newExpanded);
    } else {
      // Close all other sections for this post
      const newExpandedComments = new Set(expandedComments);
      newExpandedComments.delete(postId);
      setExpandedComments(newExpandedComments);

      const newExpandedDonors = new Set(expandedDonors);
      newExpandedDonors.delete(postId);
      setExpandedDonors(newExpandedDonors);

      const newExpandedLikes = new Set(expandedLikes);
      newExpandedLikes.delete(postId);
      setExpandedLikes(newExpandedLikes);

      const newExpandedShares = new Set(expandedShares);
      newExpandedShares.delete(postId);
      setExpandedShares(newExpandedShares);

      // Expand and load vouches
      const newExpanded = new Set(expandedVouches);
      newExpanded.add(postId);
      setExpandedVouches(newExpanded);

      // Fetch vouchers if not already loaded
      if (!vouchers[postId]) {
        try {
          const response = await fetch(`${SERVER_URL}/posts/${postId}/vouchers`);
          if (response.ok) {
            const data = await response.json();
            setVouchers({ ...vouchers, [postId]: data.vouchers });
          }
        } catch (err) {
          console.error('Error loading vouchers:', err);
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto mt-10 flex flex-col items-center gap-3 text-center">
        <div className="w-8 h-8 rounded-full border-4 border-indigo-200 border-t-indigo-500 animate-spin" />
        <p className="text-sm text-gray-400 font-medium">Loading feed...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto mt-10">
        <div className="bg-red-100 border border-red-400 text-red-700 p-4 rounded">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Use shared Navigation component */}
      <Navigation />

      {/* Mobile Category Drawer */}
      <div className={`lg:hidden fixed inset-0 z-50 flex transition-opacity duration-200 ${drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setDrawerOpen(false)} />
        <div className={`relative w-64 max-w-[80vw] bg-white h-full shadow-xl flex flex-col overflow-y-auto transition-transform duration-300 ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">Browse by Category</h3>
            <button onClick={() => setDrawerOpen(false)} className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none">×</button>
          </div>
          <div className="p-2">
            <button
              onClick={() => { setSelectedCategory(null); setShowUrgentOnly(false); setDrawerOpen(false); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${!selectedCategory && !showTimeSensitiveOnly ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              All Posts
            </button>
            <button
              onClick={() => { setShowUrgentOnly(v => !v); setSelectedCategory(null); setDrawerOpen(false); }}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${showTimeSensitiveOnly ? 'bg-red-50 text-red-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              Urgent
            </button>
            {CATEGORY_GROUPS.map(group => {
              const isFlat = group.items.length === 1 && group.items[0] === group.label;
              if (isFlat) {
                const cat = group.items[0];
                return (
                  <div key={group.label} className="mt-3">
                    <button
                      onClick={() => { setSelectedCategory(selectedCategory === cat ? null : cat); setShowUrgentOnly(false); setDrawerOpen(false); }}
                      className={`w-full text-left px-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${selectedCategory === cat ? 'text-indigo-700' : 'text-gray-700 hover:text-gray-900'}`}
                    >
                      {group.label}
                    </button>
                  </div>
                );
              }
              return (
                <div key={group.label} className="mt-3">
                  <button
                    onClick={() => toggleGroup(group.label)}
                    className="w-full flex items-center justify-between px-3 py-1 text-[10px] font-bold text-gray-700 uppercase tracking-wider hover:text-gray-900"
                  >
                    <span>{group.label}</span>
                    <span className="text-gray-400">{expandedGroups.has(group.label) ? '−' : '+'}</span>
                  </button>
                  {expandedGroups.has(group.label) && group.items.map(cat => (
                    <button
                      key={cat}
                      onClick={() => { setSelectedCategory(selectedCategory === cat ? null : cat); setShowUrgentOnly(false); setDrawerOpen(false); }}
                      className={`w-full text-left px-3 py-1.5 rounded-lg text-xs transition-colors ${selectedCategory === cat ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto mt-8 px-4 sm:px-6 lg:px-8 pb-12 flex gap-6">
        {/* Category Sidebar */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-[86px] max-h-[calc(100vh-96px)] overflow-y-auto">
            <nav>
              <div className="pb-2">
                <h3 className="text-base font-bold text-gray-900 px-3 py-3">Browse</h3>
                <button
                  onClick={() => { setSelectedCategory(null); setShowUrgentOnly(false); }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${!selectedCategory && !showTimeSensitiveOnly ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  All Posts
                </button>
                <button
                  onClick={() => { setShowUrgentOnly(v => !v); setSelectedCategory(null); }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${showTimeSensitiveOnly ? 'bg-red-50 text-red-700' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  Urgent
                </button>
                {CATEGORY_GROUPS.map(group => {
                  const isFlat = group.items.length === 1 && group.items[0] === group.label;
                  if (isFlat) {
                    const cat = group.items[0];
                    return (
                      <div key={group.label} className="mt-4">
                        <button
                          onClick={() => { setSelectedCategory(selectedCategory === cat ? null : cat); setShowUrgentOnly(false); }}
                          className={`w-full text-left px-3 pb-1 text-[11px] font-bold uppercase tracking-wider transition-colors ${selectedCategory === cat ? 'text-indigo-700' : 'text-gray-800 hover:text-gray-900'}`}
                        >
                          {group.label}
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div key={group.label} className="mt-4">
                      <button
                        onClick={() => toggleGroup(group.label)}
                        className="w-full flex items-center justify-between px-3 pb-1 text-[11px] font-bold text-gray-800 uppercase tracking-wider hover:text-gray-900"
                      >
                        <span>{group.label}</span>
                        <span className="flex-shrink-0 text-gray-400 text-base leading-none ml-2">{expandedGroups.has(group.label) ? '−' : '+'}</span>
                      </button>
                      {expandedGroups.has(group.label) && group.items.map(cat => (
                        <button
                          key={cat}
                          onClick={() => { setSelectedCategory(selectedCategory === cat ? null : cat); setShowUrgentOnly(false); }}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selectedCategory === cat ? 'bg-indigo-50 text-indigo-800 font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            </nav>
          </div>
        </aside>

        {/* Main Feed Column */}
        <div className="flex-1 max-w-2xl min-w-0">
        {/* PayPal Setup Banner */}
        {isAuthenticated && user && !user.paypal_merchant_id && (
          <div className="bg-indigo-50 border border-indigo-300 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="text-2xl">💳</div>
              <div className="flex-1">
                <h3 className="text-base font-bold text-indigo-900 mb-1">
                  Connect PayPal to Receive Gifts
                </h3>
                <p className="text-sm text-indigo-800 mb-3">
                  You cannot receive gifts until you connect your PayPal account. It only takes a few
                  minutes to set up. Gift pools go through up to a 3-day moderation hold - Gifted reviews
                  for any red flags before release, helping gifters give with confidence and keeping the community safe.
                </p>
                <button
                  onClick={() => navigate('/settings')}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold text-sm hover:bg-indigo-700 transition-all"
                >
                  Connect PayPal →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Composer Box */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 mb-4 flex items-center gap-3">
          {isAuthenticated && user
            ? <Avatar name={user.username} email={user.email} profilePhotoUrl={user.profile_photo_url} size="w-9 h-9" textSize="text-sm" />
            : <div className="w-9 h-9 rounded-full bg-gray-200 flex-shrink-0" />
          }
          <button
            onClick={() => isAuthenticated && user ? setShowGiftOptionsModal(true) : navigate('/login')}
            className="flex-1 text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm text-gray-500 transition-colors"
          >
            Share something you need...
          </button>
          <button
            onClick={() => isAuthenticated && user ? setShowGiftOptionsModal(true) : navigate('/login')}
            className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-semibold hover:bg-indigo-600 transition-colors whitespace-nowrap"
          >
            Create a Gift Pool
          </button>
        </div>

        {/* Mobile Categories Button */}
        <button
          onClick={() => setDrawerOpen(true)}
          className="lg:hidden flex items-center gap-2 px-3 py-2 mb-4 bg-white border border-gray-200 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <span>☰</span>
          <span>{showTimeSensitiveOnly ? 'Urgent' : selectedCategory ? selectedCategory : 'Browse Categories'}</span>
          {(selectedCategory || showTimeSensitiveOnly) && (
            <span onClick={e => { e.stopPropagation(); setSelectedCategory(null); setShowUrgentOnly(false); }} className="ml-1 text-gray-400 hover:text-gray-600">✕</span>
          )}
        </button>


        {/* Gift Pool Feed */}
        {posts.length > 0 && (
          <div className="space-y-3">
            {/* Filter Header */}
            {(selectedCategory || showTimeSensitiveOnly) && (
              <div className={`flex items-center justify-between p-4 border rounded-lg ${showTimeSensitiveOnly ? 'bg-red-50 border-red-200' : 'bg-indigo-50 border-indigo-200'}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${showTimeSensitiveOnly ? 'text-red-900' : 'text-indigo-900'}`}>Filtering by:</span>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${showTimeSensitiveOnly ? 'bg-red-100 text-red-700' : 'bg-indigo-100 text-indigo-800'}`}>
                    {showTimeSensitiveOnly ? 'Urgent' : selectedCategory}
                  </span>
                </div>
                <button
                  onClick={() => { setSelectedCategory(null); setShowUrgentOnly(false); }}
                  className={`text-sm font-medium transition-colors ${showTimeSensitiveOnly ? 'text-red-600 hover:text-red-800' : 'text-indigo-600 hover:text-indigo-800'}`}
                >
                  ✕ Clear filter
                </button>
              </div>
            )}

            {posts
              .filter(post => (!selectedCategory || post.category === selectedCategory))
              .map(post => (
                <GiftPoolPost
                  key={post.id}
                  post={post}
                  user={user}
                  isAuthenticated={isAuthenticated}
                  likedPosts={likedPosts}
                  sharedPosts={sharedPosts}
                  vouchedPosts={vouchedPosts}
                  commentedPosts={commentedPosts}
                  expandedDonors={expandedDonors}
                  expandedLikes={expandedLikes}
                  expandedComments={expandedComments}
                  expandedDescriptions={expandedDescriptions}
                  expandedVouches={expandedVouches}
                  expandedShares={expandedShares}
                  donors={donors}
                  likers={likers}
                  comments={comments}
                  newComment={newComment}
                  vouchers={vouchers}
                  sharers={sharers}
                  onLike={handleLike}
                  onShare={handleShare}
                  onVouch={handleVouch}
                  onToggleDonors={toggleDonors}
                  onToggleLikes={toggleLikes}
                  onToggleComments={toggleComments}
                  onToggleDescription={postId => {
                    const newExpanded = new Set(expandedDescriptions);
                    if (expandedDescriptions.has(postId)) {
                      newExpanded.delete(postId);
                    } else {
                      newExpanded.add(postId);
                    }
                    setExpandedDescriptions(newExpanded);
                  }}
                  onToggleVouches={toggleVouches}
                  onToggleShares={toggleShares}
                  onAddComment={handleAddComment}
                  onCommentChange={(postId, value) =>
                    setNewComment({ ...newComment, [postId]: value })
                  }
                  replyingTo={replyingTo}
                  replyText={replyText}
                  onReplyClick={handleReplyClick}
                  onReplyTextChange={(postId, value) =>
                    setReplyText({ ...replyText, [postId]: value })
                  }
                  onAddReply={handleAddReply}
                  onDeleteComment={handleDeleteComment}
                  onEditComment={handleEditComment}
                  selectedCategory={selectedCategory}
                  onCategoryClick={cat => { setSelectedCategory(cat); setShowUrgentOnly(false); }}
                  onUrgentClick={() => { setShowUrgentOnly(v => !v); setSelectedCategory(null); }}
                  showUrgentOnly={showTimeSensitiveOnly}
                  followingStatusLoaded={followingStatusLoaded}
                  followingUsers={followingUsers}
                  followLoading={followLoading}
                  onFollowToggle={handleFollowToggle}
                />
              ))}

            {/* Loading indicator for infinite scroll */}
            {loadingMore && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <p className="mt-2 text-sm text-gray-600">Loading more posts...</p>
              </div>
            )}

          </div>
        )}
        </div>
      </div>

      <GiftOptionsModal
        isOpen={showGiftOptionsModal}
        onClose={() => setShowGiftOptionsModal(false)}
        onSuccess={post => {
          setShowGiftOptionsModal(false);
          if (post) {
            setPosts(prev => [post, ...prev.filter(p => p.id !== post.id)]);
          }
        }}
      />
    </div>
  );
}

export default UserFeed;
